/**
 * CustomerProfileSections — Iter 71.1.
 *
 * Three big composite sections that turn the Tenant Command Center
 * into the authoritative Customer Account record:
 *
 *   <CustomerSummaryCard />    one-glance summary above the fold
 *   <ProfileSection />         Company + Contacts + Internal Owners (editable)
 *   <BillingSection />         Plan + Entitlements + Upgrade/Downgrade + History
 *   <NotificationPrefsSection /> per-customer toggles + recipients
 */
import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Building2, Users, FileStack, HardDrive, Activity, Bell, Crown,
  TrendingUp, TrendingDown, ChevronRight, Save, X, Plus, Trash2,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const CONTACT_LABELS = {
  primary_contact:   "Primary",
  secondary_contact: "Secondary",
  billing_contact:   "Billing",
  support_contact:   "Support",
};
const OWNER_LABELS = {
  account_manager:        "Account Manager",
  implementation_manager: "Implementation Manager",
  customer_success_manager: "Customer Success Manager",
  sales_owner:            "Sales Owner",
};
const PREF_LABELS = {
  demo_notifications:       "Demo notifications",
  trial_notifications:      "Trial notifications",
  onboarding_notifications: "Onboarding notifications",
  customer_health_alerts:   "Customer health alerts",
  executive_digest:         "Executive digest",
  system_notifications:     "System notifications",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

/* ── Summary card ─────────────────────────────────────────────────────── */

export function CustomerSummaryCard({ overview }) {
  const t = overview.tenant;
  const u = overview.usage || {};
  const counts = u.counts || {};
  const health = overview.health || {};
  const trialHealth = overview.trial_health || {};
  const profile = overview.profile || {};
  const owners = profile.internal_owners || {};
  const team = Object.entries(OWNER_LABELS)
    .map(([k, lab]) => ({ k, lab, email: owners[k]?.email || "" }))
    .filter((o) => o.email);
  return (
    <div className="rounded-xl border bg-[var(--fop-surface)] p-4"
          style={{ borderColor: "var(--fop-border)" }}
          data-testid="customer-summary-card">
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-2">
        Customer summary
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2.5">
        <SummaryCell label="Stage" v={t.lifecycle_stage || "active"} testid="csc-stage" />
        <SummaryCell label="Health" v={(health.health_status || trialHealth.trial_health_status || "new").replace(/_/g, " ")} testid="csc-health" />
        <SummaryCell label="Plan"  v={t.plan_code || "—"} testid="csc-plan" />
        <SummaryCell label="Users (active)" v={`${counts.active_users || 0}`} testid="csc-users" />
        <SummaryCell label="Projects" v={`${counts.projects || 0}`} testid="csc-projects" />
        <SummaryCell label="Storage" v={`${(u.storage_gb || 0).toFixed(1)} GB`} testid="csc-storage" />
        <SummaryCell label="Last activity" v={fmt(u.last_login_at || t.created_at)} testid="csc-last-activity" />
        <SummaryCell label="Assigned team" v={
          team.length === 0
            ? "—"
            : team.map((o) => o.email.split("@")[0]).slice(0, 2).join(", ")
        } testid="csc-team" />
      </div>
    </div>
  );
}

function SummaryCell({ label, v, testid }) {
  return (
    <div className="rounded-md p-2 border" data-testid={testid}
          style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] truncate">{label}</div>
      <div className="text-sm font-semibold text-[var(--fop-text)] truncate mt-0.5">{v}</div>
    </div>
  );
}

/* ── Profile (Company + Contacts + Owners) ──────────────────────────── */

export function ProfileSection({ profile, tenantId, onReload }) {
  const [draft, setDraft] = useState(() => clone(profile || {}));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setDraft(clone(profile || {})); setDirty(false); }, [profile]);

  const patchCompany = (k, v) => {
    setDraft((d) => ({ ...d, company: { ...(d.company || {}), [k]: v } }));
    setDirty(true);
  };
  const patchContact = (role, k, v) => {
    setDraft((d) => ({
      ...d, contacts: {
        ...(d.contacts || {}),
        [role]: { ...((d.contacts || {})[role] || {}), [k]: v },
      },
    }));
    setDirty(true);
  };
  const patchOwner = (role, k, v) => {
    setDraft((d) => ({
      ...d, internal_owners: {
        ...(d.internal_owners || {}),
        [role]: { ...((d.internal_owners || {})[role] || {}), [k]: v },
      },
    }));
    setDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${tenantId}/profile`, {
        company: draft.company,
        contacts: draft.contacts,
        internal_owners: draft.internal_owners,
      });
      toast.success("Customer profile saved");
      setDirty(false);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const company = draft.company || {};
  const contacts = draft.contacts || {};
  const owners = draft.internal_owners || {};

  return (
    <div className="rounded-xl border bg-[var(--fop-surface)]"
          style={{ borderColor: "var(--fop-border)" }}
          data-testid="tcc-section-profile">
      <SectionHeader title="Customer profile" action={
        <button onClick={save} disabled={!dirty || busy}
                 className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[10px] uppercase tracking-wider font-mono text-white"
                 style={{ background: dirty ? "rgba(249,115,22,0.85)" : "rgba(249,115,22,0.30)",
                          cursor: dirty ? "pointer" : "not-allowed" }}
                 data-testid="profile-save">
          <Save size={11} /> Save
        </button>
      } />
      <div className="p-4 space-y-5">
        {/* Company */}
        <div>
          <SubHead icon={Building2}>Company</SubHead>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            <Field label="Company name" value={company.company_name || ""}
                    onChange={(v) => patchCompany("company_name", v)}
                    testid="profile-company-name" />
            <Field label="Legal name" value={company.legal_name || ""}
                    onChange={(v) => patchCompany("legal_name", v)}
                    testid="profile-legal-name" />
            <Field label="DBA name" value={company.dba_name || ""}
                    onChange={(v) => patchCompany("dba_name", v)}
                    testid="profile-dba-name" />
            <Field label="Industry" value={company.industry || ""}
                    onChange={(v) => patchCompany("industry", v)}
                    testid="profile-industry" />
            <Field label="Website" value={company.website || ""}
                    onChange={(v) => patchCompany("website", v)}
                    testid="profile-website" />
            <Field label="Logo URL" value={company.logo_url || ""}
                    onChange={(v) => patchCompany("logo_url", v)}
                    testid="profile-logo-url" mono />
          </div>
          <div className="mt-2.5">
            <Label>Internal notes</Label>
            <textarea value={company.internal_notes || ""}
                       onChange={(e) => patchCompany("internal_notes", e.target.value)}
                       rows={3}
                       className="w-full px-3 py-2 rounded-md border bg-transparent text-sm"
                       style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
                       data-testid="profile-internal-notes" />
          </div>
        </div>

        {/* Contacts */}
        <div>
          <SubHead icon={Users}>Contacts</SubHead>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            {Object.entries(CONTACT_LABELS).map(([role, label]) => {
              const c = contacts[role] || {};
              return (
                <div key={role} className="rounded-md border p-2.5"
                      style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}
                      data-testid={`profile-contact-${role}`}>
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-primary)] mb-1.5">{label}</div>
                  <Field label="Name" value={c.name || ""} onChange={(v) => patchContact(role, "name", v)} testid={`profile-${role}-name`} />
                  <Field label="Title" value={c.title || ""} onChange={(v) => patchContact(role, "title", v)} testid={`profile-${role}-title`} />
                  <Field label="Email" value={c.email || ""} onChange={(v) => patchContact(role, "email", v)} testid={`profile-${role}-email`} type="email" />
                  <Field label="Phone" value={c.phone || ""} onChange={(v) => patchContact(role, "phone", v)} testid={`profile-${role}-phone`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Internal owners */}
        <div>
          <SubHead icon={Crown}>Internal team ownership</SubHead>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            {Object.entries(OWNER_LABELS).map(([role, label]) => {
              const o = owners[role] || {};
              return (
                <div key={role} className="rounded-md border p-2.5"
                      style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}
                      data-testid={`profile-owner-${role}`}>
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-primary)] mb-1.5">{label}</div>
                  <Field label="Email" value={o.email || ""} onChange={(v) => patchOwner(role, "email", v)} testid={`profile-${role}-email`} type="email" />
                  <Field label="Name" value={o.name || ""} onChange={(v) => patchOwner(role, "name", v)} testid={`profile-${role}-name`} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Billing (Plan / Entitlements / Upgrade-Downgrade / History) ────── */

export function BillingSection({ tenant, plan, entitlements, planHistory, plans, tenantId, onReload }) {
  const currentCode = tenant.plan_code || "—";
  const unassigned = !tenant.plan_code;
  const upgradePaths = unassigned
    ? (plans || []).filter((p) => !p.is_trial)
    : (plan?.upgrade_paths || []).map((c) => byCode(plans, c)).filter(Boolean);
  const downgradePaths = (plan?.downgrade_paths || []).map((c) => byCode(plans, c)).filter(Boolean);
  const [busy, setBusy] = useState(false);
  const [showDialog, setShowDialog] = useState(null); // { direction, target }

  const submit = async (direction, target_plan, reason) => {
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenantId}/plan/${direction}`,
                       { target_plan, reason });
      toast.success(unassigned ? `Plan assigned: ${target_plan}` : `Plan ${direction}d to ${target_plan}`);
      setShowDialog(null);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border bg-[var(--fop-surface)]"
          style={{ borderColor: "var(--fop-border)" }}
          data-testid="tcc-section-billing">
      <SectionHeader title="Plan & limits" badge="Admin configuration" />
      <div className="p-4 space-y-4">
        <p className="text-xs text-[var(--fop-text-2)]">
          Plan assignment controls product limits. It does not create, cancel, or
          change a Stripe subscription. Configure complimentary access separately above.
        </p>
        <div className="rounded-md border p-3"
              style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Current plan</div>
              <div className="text-lg font-semibold text-[var(--fop-text)] mt-0.5"
                    data-testid="billing-current-plan">
                {plan?.label || currentCode}
              </div>
              {plan?.tier && (
                <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider mt-0.5">tier · {plan.tier}{plan.is_trial ? " · trial" : ""}</div>
              )}
            </div>
            {plan?.description && (
              <div className="text-xs text-[var(--fop-text-2)] flex-1 min-w-0 sm:text-right">{plan.description}</div>
            )}
          </div>
          {/* Entitlements grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="billing-entitlements">
            {(entitlements?.rows || []).map((r) => {
              const tone = r.status === "blocked" ? "#fca5a5"
                         : r.status === "warning" ? "#fbbf24"
                         :                            "#86efac";
              return (
                <div key={r.limit} className="rounded-md p-2 border"
                      style={{ borderColor: "var(--fop-border)" }}
                      data-testid={`billing-entitlement-${r.limit}`}>
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] capitalize">{r.limit.replace(/_/g, " ")}</div>
                  <div className="text-sm font-mono mt-0.5">
                    <span style={{ color: tone }}>{r.current}</span>
                    <span className="text-[var(--fop-text-3)]"> / {r.unlimited ? "∞" : r.cap}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upgrade / Downgrade */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <PathBlock
            kind="upgrade" icon={TrendingUp} fg="#86efac"
            title={unassigned ? "Assign an initial plan" : "Upgrade paths"}
            paths={upgradePaths}
            onSelect={(p) => setShowDialog({ direction: "upgrade", target: p })}
            testid="billing-upgrade-paths"
          />
          <PathBlock
            kind="downgrade" icon={TrendingDown} fg="#fbbf24"
            paths={downgradePaths}
            onSelect={(p) => setShowDialog({ direction: "downgrade", target: p })}
            testid="billing-downgrade-paths"
          />
        </div>

        {/* History */}
        <div>
          <SubHead icon={Activity}>Plan history</SubHead>
          {(planHistory || []).length === 0 ? (
            <div className="text-xs text-[var(--fop-text-3)] mt-2"
                  data-testid="billing-plan-history-empty">No plan changes recorded yet.</div>
          ) : (
            <div className="space-y-1 mt-2 text-xs" data-testid="billing-plan-history">
              {planHistory.map((h, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-md border"
                      style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}>
                  {h.direction === "upgrade"
                    ? <TrendingUp size={12} className="text-emerald-300 mt-0.5" />
                    : h.direction === "downgrade"
                      ? <TrendingDown size={12} className="text-amber-300 mt-0.5" />
                      : <ChevronRight size={12} className="text-[var(--fop-text-3)] mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[var(--fop-text-2)] font-mono">
                      {h.old_plan || "—"} → <span className="text-[var(--fop-text)]">{h.new_plan}</span>
                    </div>
                    <div className="text-[var(--fop-text-3)] font-mono text-[10px]">
                      {fmt(h.changed_at)} by {h.changed_by || "—"}{h.reason ? ` · ${h.reason}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showDialog && (
        <PlanChangeDialog dialog={showDialog} busy={busy}
                            onCancel={() => setShowDialog(null)}
                            onSubmit={(reason) => submit(showDialog.direction, showDialog.target.code, reason)} />
      )}
    </div>
  );
}

function PathBlock({ kind, paths, icon: Icon, fg, onSelect, testid, title }) {
  return (
    <div className="rounded-md border p-3"
          style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}
          data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] flex items-center gap-1.5 mb-2"
            style={{ color: fg }}>
        <Icon size={11} /> {title || (kind === "upgrade" ? "Upgrade paths" : "Downgrade paths")}
      </div>
      {paths.length === 0 ? (
        <div className="text-xs text-[var(--fop-text-3)]">
          {kind === "upgrade" ? "No upgrade path from this plan." : "No downgrade path from this plan."}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {paths.map((p) => (
            <button key={p.code} onClick={() => onSelect(p)}
                     className="h-8 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                     style={{ borderColor: fg, color: fg, background: `${fg}11` }}
                     data-testid={`${kind}-path-${p.code}`}>
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PlanChangeDialog({ dialog, busy, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  return (
    <ModalShell title={`${capitalize(dialog.direction)} → ${dialog.target.label}`} onClose={onCancel}
                testid={`billing-${dialog.direction}-dialog`}>
      <div className="text-xs text-[var(--fop-text-2)] mb-3 space-y-1">
        <div>New limits will apply immediately:</div>
        <div className="grid grid-cols-2 gap-1 font-mono text-xs mt-2">
          {Object.entries(dialog.target.limits || {}).map(([k, v]) => (
            <div key={k}>
              <span className="text-[var(--fop-text-3)]">{k.replace(/_/g, " ")}:</span>{" "}
              <span className="text-[var(--fop-text)]">{v === 0 ? "∞" : v}</span>
            </div>
          ))}
        </div>
      </div>
      <Label>Reason (optional)</Label>
      <input value={reason} onChange={(e) => setReason(e.target.value)}
              className="h-10 px-3 rounded-md border bg-transparent w-full text-sm"
              style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
              data-testid="billing-plan-change-reason" />
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onSubmit(reason)} disabled={busy}
                className="h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                style={{ background: "rgba(249,115,22,0.85)" }}
                data-testid="billing-plan-change-submit">
          Apply {dialog.direction}
        </button>
        <button onClick={onCancel} className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">Cancel</button>
      </div>
    </ModalShell>
  );
}

/* ── Notification preferences ──────────────────────────────────────── */

export function NotificationPrefsSection({ profile, tenantId, onReload }) {
  const [prefs, setPrefs] = useState(profile?.notification_prefs || {});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { setPrefs(profile?.notification_prefs || {}); setDirty(false); }, [profile]);

  const setEnabled = (k, v) => {
    setPrefs((p) => ({ ...p, [k]: { ...(p[k] || {}), enabled: v } }));
    setDirty(true);
  };
  const addRecipient = (k, email) => {
    if (!email.trim()) return;
    setPrefs((p) => ({
      ...p, [k]: { ...(p[k] || {}),
                   recipients: [...((p[k] || {}).recipients || []), email.trim()] },
    }));
    setDirty(true);
  };
  const removeRecipient = (k, i) => {
    setPrefs((p) => {
      const list = [...((p[k] || {}).recipients || [])];
      list.splice(i, 1);
      return { ...p, [k]: { ...(p[k] || {}), recipients: list } };
    });
    setDirty(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${tenantId}/profile`, { notification_prefs: prefs });
      toast.success("Notification preferences saved");
      setDirty(false);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border bg-[var(--fop-surface)]"
          style={{ borderColor: "var(--fop-border)" }}
          data-testid="tcc-section-notifications">
      <SectionHeader title="Notification preferences" action={
        <button onClick={save} disabled={!dirty || busy}
                 className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[10px] uppercase tracking-wider font-mono text-white"
                 style={{ background: dirty ? "rgba(249,115,22,0.85)" : "rgba(249,115,22,0.30)",
                          cursor: dirty ? "pointer" : "not-allowed" }}
                 data-testid="notif-save">
          <Save size={11} /> Save
        </button>
      } />
      <div className="p-4 space-y-3">
        {Object.entries(PREF_LABELS).map(([key, label]) => {
          const pref = prefs[key] || { enabled: true, recipients: [] };
          return (
            <PrefRow key={key} k={key} label={label} pref={pref}
                       onEnabled={setEnabled} onAdd={addRecipient} onRemove={removeRecipient} />
          );
        })}
      </div>
    </div>
  );
}

function PrefRow({ k, label, pref, onEnabled, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  const add = () => { onAdd(k, draft); setDraft(""); };
  return (
    <div className="rounded-md border p-3"
          style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}
          data-testid={`notif-pref-${k}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[var(--fop-text)]">{label}</div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!pref.enabled}
                  onChange={(e) => onEnabled(k, e.target.checked)}
                  data-testid={`notif-pref-${k}-enabled`} />
          <span className="text-[11px] font-mono uppercase tracking-wider"
                 style={{ color: pref.enabled ? "#86efac" : "var(--fop-text-3)" }}>
            {pref.enabled ? "on" : "off"}
          </span>
        </label>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {(pref.recipients || []).map((r, i) => (
          <span key={i}
                 className="inline-flex items-center gap-1 px-2 h-7 rounded-full border text-xs font-mono"
                 style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}>
            {r}
            <button onClick={() => onRemove(k, i)} className="ml-1 text-[var(--fop-text-3)] hover:text-red-300"
                     data-testid={`notif-pref-${k}-remove-${i}`}>
              <X size={10} />
            </button>
          </span>
        ))}
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                placeholder="add email + Enter"
                className="h-7 px-2 rounded-md border bg-transparent text-xs"
                style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
                data-testid={`notif-pref-${k}-add`} />
        <button onClick={add} className="h-7 px-2 rounded-md border text-[10px] uppercase font-mono"
                 style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                 data-testid={`notif-pref-${k}-add-btn`}>
          <Plus size={11} />
        </button>
      </div>
    </div>
  );
}

/* ── Reusable building blocks (local, not exported) ─────────────────── */

function SectionHeader({ title, action, badge }) {
  return (
    <div className="px-4 py-3 border-b flex items-center justify-between gap-2 flex-wrap"
          style={{ borderColor: "var(--fop-border)" }}>
      <div className="flex items-center gap-2">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-2)]">{title}</div>
        {badge && (
          <span className="text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full"
                 style={{ background: "rgba(249,115,22,0.10)", color: "var(--fop-primary)", border: "1px solid rgba(249,115,22,0.30)" }}>
            {badge}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

function SubHead({ icon: Icon, children }) {
  return (
    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] flex items-center gap-1.5">
      <Icon size={11} /> {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", mono, testid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type}
              className={`h-9 px-3 rounded-md border bg-transparent w-full text-sm ${mono ? "font-mono text-xs" : ""}`}
              style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
              data-testid={testid} />
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{children}</div>;
}

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          data-testid={testid} onClick={onClose}>
      <div className="bg-[var(--fop-surface)] border rounded-xl shadow-2xl w-full max-w-md p-5"
            style={{ borderColor: "var(--fop-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[var(--fop-text)] font-semibold">{title}</div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const clone = (o) => JSON.parse(JSON.stringify(o || {}));
const capitalize = (s) => (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
const byCode = (plans, code) => (plans || []).find((p) => p.code === code) || { code, label: code, limits: {} };
